import { useState } from "react";
import { AdminHeader } from "../../components/header";
import Editor from "react-simple-wysiwyg";
import {
  AdditionalQuestions,
  EventData,
  TicketTypeRawData,
} from "../../utils/types";
import { CreateTicketModal } from "../../components/admin/modals/create-ticket";
import { CreateQuestionModal } from "../../components/admin/modals/create-question";
import { toast } from "react-toastify";
import { formatMoney, isEmailValid, NAIRA_SYMBOL } from "../../utils";
import moment from "moment";
import { PhoneNumberUtil } from "google-libphonenumber";
import { PhoneInput } from "react-international-phone";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import {
  firebaseAuth,
  firebaseStorage,
  firestoredb,
} from "../../utils/firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { signInWithEmailAndPassword } from "firebase/auth";
import { Loading } from "../../components/loading";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

export function CreateEvent() {
  const [selectedQuestionToEdit, setSelectedQuestionToEdit] =
    useState<AdditionalQuestions | null>(null);
  const [selectedTicketToEdit, setSelectedTicketToEdit] =
    useState<TicketTypeRawData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState<EventData>({
    id: "",
    eventName: "",
    eventDescription: "",
    organiserName: "",
    venue: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    supportContactName: "",
    supportContactPhoneNumber: "",
    supportContactEmail: "",
    promotionalText: "",
    website: "",
    tiktok: "",
    linkedIn: "",
    facebook: "",
    instagram: "",
    eventImage: "",
    x: "",
    questions: [],
    ticketTypes: [],
    picture: "",
    price: 0,
  });

  const handleSaveEditedTicket = (ticket: TicketTypeRawData) => {
    const ticketIndex = eventData.ticketTypes.findIndex(
      (t) => t.id === ticket.id
    );
    if (ticketIndex !== -1) {
      eventData.ticketTypes[ticketIndex] = ticket;
    }
    setEventData({ ...eventData });
  };

  const handleSaveTicket = (ticket: TicketTypeRawData) => {
    setEventData({
      ...eventData,
      ticketTypes: [...eventData.ticketTypes, ticket],
    });
  };

  const handleDeleteQuestion = (question: AdditionalQuestions) => {
    setEventData({
      ...eventData,
      questions: eventData.questions.filter(
        (q) => q.question !== question.question
      ),
    });
    toast.error("Question deleted");
  };

  const handleDeleteTicket = (ticket: TicketTypeRawData) => {
    setEventData({
      ...eventData,
      ticketTypes: eventData.ticketTypes.filter((t) => t.id !== ticket.id),
    });
    toast.error("Ticket deleted");
  };

  const disableCreateEventButton =
    !eventData.eventName ||
    !eventData.eventDescription ||
    !eventData.organiserName ||
    !eventData.venue ||
    !eventData.startDate ||
    !eventData.startTime ||
    !eventData.endDate ||
    !eventData.endTime ||
    !eventData.supportContactName ||
    !eventData.supportContactPhoneNumber ||
    !eventData.supportContactEmail ||
    eventData.ticketTypes.length === 0 ||
    !eventData.eventImage;

  const validateEventData = () => {
    if (moment(eventData.startDate).isAfter(moment(eventData.endDate))) {
      toast.error("Start date must be before end date");
      return false;
    }
    // if (!moment(eventData.startTime).isValid()) {
    //   toast.error("Start time is invalid");
    //   return false;
    // }
    // if (!moment(eventData.endTime).isValid()) {
    //   toast.error("End time is invalid");
    //   return false;
    // }

    if (moment(eventData.startDate).isBefore(moment())) {
      toast.error("Start date must be in the future");
      return false;
    }
    if (!isPhoneValid(eventData.supportContactPhoneNumber)) {
      toast.error("Support contact phone number is invalid");
      return false;
    }
    if (!isEmailValid(eventData.supportContactEmail)) {
      toast.error("Support contact email is invalid");
      return false;
    }
    if (!eventData.id) {
      toast.error("Event id is required");
      return false;
    }
    return true;
  };

  function generateShortEventCode(eventName: string, organizerName: string) {
    const year = new Date().getFullYear().toString().slice(-2);
    const random = Math.random().toString(36).substr(2, 2).toUpperCase();

    const cleanEvent = eventName
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3);
    const cleanOrganizer = organizerName
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3);

    const code = `${cleanEvent}${cleanOrganizer}${year}${random}`; // max ~10 chars

    return code.slice(0, 10); // ensure max 10 chars
  }

  const handleSaveEvent = async () => {
    try {
      if (!validateEventData()) return;
      if (!file) {
        toast.error("Please upload an image");
        return;
      }
      setLoading(true);
      const [startYear, startMonth, startDay] = eventData.startDate.split("-");
      const [startHour, startMinute] = eventData.startTime.split(":");
      const [endYear, endMonth, endDay] = eventData.endDate.split("-");
      const [endHour, endMinute] = eventData.endTime.split(":");

      const startJsDate = new Date(
        Number(startYear),
        Number(startMonth) - 1, // JS months are zero-indexed
        Number(startDay),
        Number(startHour),
        Number(startMinute)
      );
      const startDateFirebaseTimestamp = Timestamp.fromDate(startJsDate);
      const endJsDate = new Date(
        Number(endYear),
        Number(endMonth) - 1, // JS months are zero-indexed
        Number(endDay),
        Number(endHour),
        Number(endMinute)
      );
      const endDateFirebaseTimestamp = Timestamp.fromDate(endJsDate);

      const storageRef = ref(
        firebaseStorage,
        `pictures/${eventData.eventName}}`
      );
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const body = {
        about: eventData.eventDescription,
        applyDingFee: true,
        bearDingFee: false,
        date: startDateFirebaseTimestamp,
        endDate: endDateFirebaseTimestamp,
        linkTitle: eventData.promotionalText,
        organiser: eventData.organiserName,
        picture: downloadURL,
        scannerCode: generateShortEventCode(
          eventData.eventName,
          eventData.organiserName
        ),
        soldOut: false,
        social: {
          instagram: eventData.instagram,
          linkedin: eventData.linkedIn,
          facebook: eventData.facebook,
          tiktok: eventData.tiktok,
          x: eventData.x,
        },
        title: eventData.eventName,
        venue: eventData.venue,
        website: eventData.website,
        types: eventData.ticketTypes,
        nonRequiredInfo: eventData.questions
          .filter((q) => !q.required)
          .map((el) => el.question),
        otherRequiredInfo: eventData.questions
          .filter((q) => q.required)
          .map((el) => el.question),
        reach: `${eventData.supportContactName} - ${eventData.supportContactPhoneNumber} - ${eventData.supportContactEmail}`,
        // id: eventData.eventName.toLowerCase().replace(/ /g, "-"),
        id: eventData.id.toLowerCase(),
      };

      await signInWithEmailAndPassword(
        firebaseAuth,
        process.env.REACT_APP_FIREBASE_AUTH_EMAIL!,
        process.env.REACT_APP_FIREBASE_AUTH_PASSWORD!
      );
      await setDoc(doc(firestoredb, "events", body.id), body);
      // we should send email
      toast.success("Event created successfully");
      window.location.reload();
    } catch (error) {
      console.log({ error });
      toast.error(
        error instanceof Error
          ? error.message
          : "Sorry, event cannot be created at the moment. Please try again later!"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='container-fluid create-event'>
      <AdminHeader type='internal-admin' />
      <div className='create-event__header'>
        <h1>Create Event</h1>
        <button
          className='btn'
          onClick={() => (window.location.href = "/internal/dashboard")}
        >
          <i className='bi bi-x'></i>
          <p>Cancel</p>
        </button>
      </div>
      <div className='row create-event__container'>
        <div className='col-lg-7 col-12 create-event__form-container'>
          <div className='create-event__form-container__event-details'>
            <h2 className='title'>Event Details</h2>
            <div className='form-group'>
              <label htmlFor='event-name'>
                Event Name <span className='required'>*</span>
              </label>
              <input
                type='text'
                id='event-name'
                placeholder='Event name'
                onChange={(e) =>
                  setEventData({ ...eventData, eventName: e.target.value })
                }
              />
            </div>
            <div className='form-group'>
              <label>
                Event ID <span className='required'>*</span>
              </label>
              <div className='form-group__event-id'>
                <p>https://ticketsbydingpay.com/events/</p>
                <input
                  type='text'
                  placeholder='event_id'
                  value={eventData.id.toLowerCase()}
                  onChange={({ target: { value } }) =>
                    setEventData({ ...eventData, id: value.toLowerCase() })
                  }
                />
              </div>
            </div>
            <div className='form-group'>
              <label htmlFor='event-description'>
                Event description <span className='required'>*</span>
              </label>
              <Editor
                value={eventData.eventDescription}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    eventDescription: e.target.value,
                  })
                }
                title='Event Description'
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-organiser-name'>
                Organiser Name <span className='required'>*</span>
              </label>
              <input
                type='text'
                id='event-organiser-name'
                placeholder='Organiser name'
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    organiserName: e.target.value,
                  })
                }
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-venue'>
                Venue <span className='required'>*</span>
              </label>
              <input
                type='text'
                id='event-venue'
                placeholder='Venue'
                onChange={(e) =>
                  setEventData({ ...eventData, venue: e.target.value })
                }
              />
            </div>
            <div className='row'>
              <div className='col-8'>
                <div className='form-group'>
                  <label htmlFor='event-start-date'>
                    Start Date <span className='required'>*</span>
                  </label>
                  <input
                    aria-label='Date and time'
                    id='event-start-date'
                    placeholder='Start date'
                    type='date'
                    value={eventData.startDate}
                    onChange={(e) =>
                      setEventData({ ...eventData, startDate: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className='col-4'>
                <div className='form-group'>
                  <label htmlFor='event-start-time'>
                    Start Time <span className='required'>*</span>
                  </label>
                  <input
                    aria-label='Date and time'
                    id='event-start-time'
                    placeholder='Start time'
                    type='time'
                    value={eventData.startTime}
                    onChange={(e) =>
                      setEventData({ ...eventData, startTime: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className='row'>
              <div className='col-8'>
                <div className='form-group'>
                  <label htmlFor='event-end-date'>
                    End Date <span className='required'>*</span>
                  </label>
                  <input
                    aria-label='Date and time'
                    id='event-end-date'
                    placeholder='End date'
                    type='date'
                    value={eventData.endDate}
                    onChange={(e) =>
                      setEventData({ ...eventData, endDate: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className='col-4'>
                <div className='form-group'>
                  <label htmlFor='event-end-time'>
                    End Time <span className='required'>*</span>
                  </label>
                  <input
                    aria-label='Date and time'
                    id='event-end-time'
                    placeholder='End time'
                    type='time'
                    value={eventData.endTime}
                    onChange={(e) =>
                      setEventData({ ...eventData, endTime: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='create-event__form-container__tickets-types'>
            <h2 className='title'>Tickets</h2>
            <div className='create-event__form-container__tickets-types__list'>
              {eventData.ticketTypes.map((ticket, index) => (
                <div className='ticket' key={index}>
                  <div className='ticket-details'>
                    <h4>{ticket?.title}</h4>
                    <h6>
                      {ticket.price > 0
                        ? `${NAIRA_SYMBOL} ${formatMoney(ticket.price)}`
                        : "Free"}
                    </h6>
                  </div>
                  <div className='actions'>
                    <h6
                      className='btn'
                      data-bs-toggle='modal'
                      data-bs-target='#create-ticket-modal'
                      id='create-ticket-modal-btn'
                      onClick={() => setSelectedTicketToEdit(ticket)}
                    >
                      Edit ticket
                    </h6>
                    <button
                      className='btn'
                      onClick={() => handleDeleteTicket(ticket)}
                    >
                      <i className='bi bi-trash3'></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              className='btn btn-link'
              data-bs-toggle='modal'
              data-bs-target='#create-ticket-modal'
              id='create-ticket-modal-btn'
              onClick={() => setSelectedTicketToEdit(null)}
            >
              Add ticket
            </button>
          </div>
          <div className='create-event__form-container__tickets-types'>
            <h2 className='title'>Additional Questions</h2>
            <div className='create-event__form-container__tickets-types__list'>
              {eventData.questions.map((question, index) => (
                <div className='ticket' key={index}>
                  <div className='ticket-details'>
                    <h4>
                      {question?.question}
                      <span className='required'>
                        {question?.required ? "*" : ""}
                      </span>
                    </h4>
                  </div>
                  <div className='actions'>
                    <h6
                      onClick={() => setSelectedQuestionToEdit(question)}
                      data-bs-toggle='modal'
                      data-bs-target='#create-question-modal'
                    >
                      Edit question
                    </h6>
                    <button
                      className='btn'
                      onClick={() => handleDeleteQuestion(question)}
                    >
                      <i className='bi bi-trash3'></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              className='btn btn-link'
              data-bs-toggle='modal'
              data-bs-target='#create-question-modal'
              id='create-question-modal-btn'
              onClick={() => setSelectedQuestionToEdit(null)}
            >
              Add question
            </button>
          </div>
          <div className='create-event__form-container__other-info'>
            <h2 className='title'>Support Contact</h2>
            <div className='form-group'>
              <label htmlFor='event-name'>
                Support contact name <span className='required'>*</span>
              </label>
              <input
                type='text'
                id='support-contact-name'
                placeholder='Support contact name'
                value={eventData.supportContactName}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    supportContactName: e.target.value,
                  })
                }
              />
            </div>
            <div className='row'>
              <div className='col-md-6 col-12'>
                <div className='form-group'>
                  <label htmlFor='support-contact-phone-number'>
                    Support contact phone number{" "}
                    <span className='required'>*</span>
                  </label>
                  <PhoneInput
                    defaultCountry='ng'
                    value={eventData.supportContactPhoneNumber ?? ""}
                    onChange={(phone) => {
                      setEventData({
                        ...eventData,
                        supportContactPhoneNumber: phone,
                      });
                    }}
                  />
                  {/* <input
                      type="text"
                      id="event-name"
                      placeholder="Event name"
                      value={eventData.supportContactPhoneNumber}
                      onChange={(e) =>
                        setEventData({
                          ...eventData,
                          supportContactPhoneNumber: e.target.value,
                        })
                      }
                    /> */}
                </div>
              </div>
              <div className='col-md-6 col-12'>
                <div className='form-group'>
                  <label htmlFor='support-contact-email-address'>
                    Support contact email address{" "}
                    <span className='required'>*</span>
                  </label>
                  <input
                    type='text'
                    id='support-contact-email-address'
                    placeholder='Support Contact Email Address'
                    value={eventData.supportContactEmail}
                    onChange={(e) =>
                      setEventData({
                        ...eventData,
                        supportContactEmail: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='create-event__form-container__promotional-text'>
            <h2 className='title'>Promotional Text</h2>
            <p>Promotional Text</p>
            <Editor
              value={eventData.promotionalText}
              onChange={(e) =>
                setEventData({
                  ...eventData,
                  promotionalText: e.target.value,
                })
              }
              title='Promotional Text'
            />
          </div>
          <div className='create-event__form-container__social-links'>
            <h2 className='title'>Social Links</h2>
            <div className='form-group'>
              <label htmlFor='event-name'>Website</label>
              <input
                type='text'
                id='website'
                placeholder='Website'
                value={eventData.website}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    website: e.target.value,
                  })
                }
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-name'>Instagram</label>
              <input
                type='text'
                id='event-name'
                placeholder='Instagram'
                value={eventData.instagram}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    instagram: e.target.value,
                  })
                }
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-name'>X (formerly Twitter)</label>
              <input
                type='text'
                id='event-name'
                placeholder='X (formerly Twitter)'
                value={eventData.x}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    x: e.target.value,
                  })
                }
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-name'>Tiktok</label>
              <input
                type='text'
                id='event-name'
                placeholder='Tiktok'
                value={eventData.tiktok}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    tiktok: e.target.value,
                  })
                }
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-name'>LinkedIn</label>
              <input
                type='text'
                id='event-name'
                placeholder='LinkedIn'
                value={eventData.linkedIn}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    linkedIn: e.target.value,
                  })
                }
              />
            </div>
            <div className='form-group'>
              <label htmlFor='event-name'>Facebook</label>
              <input
                type='text'
                id='event-name'
                placeholder='Facebook'
                value={eventData.facebook}
                onChange={(e) =>
                  setEventData({
                    ...eventData,
                    facebook: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <button
            className='btn create-event'
            onClick={handleSaveEvent}
            disabled={disableCreateEventButton || loading}
          >
            {loading ? <Loading /> : "Create Event"}
          </button>
        </div>
        <div className='col-lg-5 col-12 upload-event-image-container'>
          <div className='upload-img-container'>
            <div className='image-container'>
              <img
                src={eventData?.eventImage || "https://placehold.co/390x396"}
                alt='event'
                className='img-fluid'
              />
              <input
                type='file'
                id='imageInput'
                accept='image/*'
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setFile(file);
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setEventData({
                        ...eventData,
                        eventImage: e.target?.result as string,
                      });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
            <button
              className='btn'
              onClick={() => document.getElementById("imageInput")?.click()}
            >
              {eventData.eventImage ? "Change Image" : "Upload Image"}
            </button>
          </div>
        </div>
      </div>
      <CreateTicketModal
        handleSaveTicket={handleSaveTicket}
        selectedTicketToEdit={selectedTicketToEdit}
        handleSaveEditedTicket={handleSaveEditedTicket}
      />
      <CreateQuestionModal
        saveQuestion={(data: AdditionalQuestions) =>
          setEventData({
            ...eventData,
            questions: [...eventData.questions, data],
          })
        }
        questions={eventData.questions}
        selectedQuestionToEdit={selectedQuestionToEdit}
        editQuestion={(data: AdditionalQuestions[]) =>
          setEventData({
            ...eventData,
            questions: [...data],
          })
        }
      />
    </div>
  );
}
